/**
 * useDomains — estado, efeitos e ações do card "Domínio próprio".
 *
 * A TRADUÇÃO (resposta do servidor → ViewModel) mora em `domainsVmBuilder.js`, que é puro e
 * testável. Aqui fica só o que precisa de React.
 *
 * ⚠️ REGRA DESTE HOOK: **ele não é dono de nenhum estado de compra.** Quem sabe se há PIX aberto,
 * se o pagamento caiu e em que passo o registro está é o servidor, via `GET /v1/domains/status`.
 * Aqui só existem: o que o cliente está escolhendo agora (nome, página da raiz, texto digitado) e
 * os diálogos. Guardar etapa de compra no navegador foi exatamente o que quebrou na primeira
 * compra real — reload = cobrança perdida.
 */
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import domainsService from '../../services/domainsService';
import { cpfMask, unmaskCpf } from '../../utils/masks';
import { isValidCpf } from '../../utils/cpfValidator';
import { montarVm, MOTIVO_LABEL, ERRO_COMPRA_LABEL } from './domainsVmBuilder';

/** Com o QR na tela, cada tick também cutuca a confirmação no Billing. 5s = o mesmo do checkout de pacotes. */
const POLL_PAGAMENTO_MS = 5000;
/** Apontar DNS leva de minutos a horas — de 30 em 30s já é generoso. */
const POLL_PROVISIONAMENTO_MS = 30000;
/** O servidor recusa checagens em rajada (429): o debounce transforma "digitar" em uma consulta só. */
const DEBOUNCE_CHECAGEM_MS = 700;
/** Quanto tempo o "copiado!" fica na tela. */
const FEEDBACK_COPIA_MS = 2000;

const CUSTOM_VAZIO = { input: '', status: 'idle', message: null, selected: false, canUse: false };
const CPF_FECHADO = { open: false, value: '', error: null, busy: false };
const CANCEL_FECHADO = { open: false, busy: false, error: null };
const RAIZ_FECHADO = { open: false, busy: false, domainId: null, selectedPageId: null, error: null };

export default function useDomains() {
  // Retrato vindo do servidor. `undefined` = ainda não buscamos; `null` = 204 (não liberado).
  const [status, setStatus] = useState(undefined);
  const [carregando, setCarregando] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [actionFeedback, setActionFeedback] = useState(null);
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [dispensado, setDispensado] = useState(false);
  const [lidoEm, setLidoEm] = useState(null);

  // Escolhas em andamento — o único estado que é legítimo viver aqui.
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [rootPageId, setRootPageId] = useState(null);
  const [custom, setCustom] = useState(CUSTOM_VAZIO);

  // Feedback efêmero de cópia (vale tanto para o PIX quanto para os endereços).
  const [copiado, setCopiado] = useState(null);

  const [cpfDialog, setCpfDialog] = useState(CPF_FECHADO);
  const [cancelDialog, setCancelDialog] = useState(CANCEL_FECHADO);
  const [rootDialog, setRootDialog] = useState(RAIZ_FECHADO);

  const debounceRef = useRef(null);
  const ultimaBuscaRef = useRef('');
  const copiaTimerRef = useRef(null);

  /**
   * Busca o estado. `silencioso` = tick de poll: não pisca spinner nem apaga o que está na tela
   * (um erro de rede momentâneo não pode sumir com o QR que o cliente está pagando).
   */
  const carregar = useCallback(async (silencioso = false) => {
    if (!silencioso) { setCarregando(true); setErrorMessage(null); }
    else setRefreshing(true);
    try {
      const dados = await domainsService.getStatus();
      setStatus(dados);
      setErrorMessage(null);
      setLidoEm(Date.now());
      return dados;
    } catch {
      if (!silencioso) setErrorMessage('Não foi possível carregar seus domínios agora.');
      return undefined;
    } finally {
      setCarregando(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // A raiz sugerida vem do servidor (empresa > profissional > link na bio) e só é aplicada
  // enquanto o cliente ainda não escolheu — trocar a escolha dele a cada poll seria sabotagem.
  useEffect(() => {
    if (rootPageId || !status?.pages?.length) return;
    setRootPageId(status.pages.find((p) => p.suggestedRoot)?.pageId ?? status.pages[0].pageId);
  }, [status, rootPageId]);

  // O 1º nome já vem marcado — ele é o "mais parecido com o seu negócio" (rank 1 do gerador).
  //
  // ⚠️ Sem isto a tela abria com NENHUMA opção marcada e o título mostrando o texto de exemplo
  // "seu domínio", como se a oferta não tivesse nome nenhum: o cliente precisava adivinhar que
  // era ele quem tinha de clicar para a proposta ganhar corpo. O botão de comprar também nascia
  // desabilitado (`canPurchase` exige domínio + raiz), e a raiz já vinha escolhida — só o nome
  // não vinha, o que era incoerente.
  //
  // Mesma regra da raiz: só preenche enquanto o cliente não escolheu, senão cada poll desfaria
  // a escolha dele.
  useEffect(() => {
    if (selectedDomain || !status?.suggestions?.length) return;
    setSelectedDomain(status.suggestions[0].domain);
  }, [status, selectedDomain]);

  // Poll: rápido com PIX na tela (o tick também confirma o pagamento no servidor), lento enquanto
  // um domínio está sendo provisionado, parado quando não há nada acontecendo.
  const emProvisionamento = useMemo(
    () => (status?.domains || []).some((d) => !['active', 'failed', 'expired'].includes(d.status)),
    [status],
  );
  const intervalo = status?.openPurchase ? POLL_PAGAMENTO_MS : emProvisionamento ? POLL_PROVISIONAMENTO_MS : 0;

  useEffect(() => {
    if (!intervalo) return undefined;
    const id = setInterval(() => {
      // Aba em segundo plano não precisa de poll — e cada tick com PIX aberto bate no gateway.
      if (document.visibilityState === 'visible') carregar(true);
    }, intervalo);
    return () => clearInterval(id);
  }, [intervalo, carregar]);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (copiaTimerRef.current) clearTimeout(copiaTimerRef.current);
  }, []);

  const copiar = useCallback((texto, chave) => {
    if (!texto) return;
    navigator.clipboard?.writeText(texto);
    setCopiado(chave);
    if (copiaTimerRef.current) clearTimeout(copiaTimerRef.current);
    copiaTimerRef.current = setTimeout(() => setCopiado(null), FEEDBACK_COPIA_MS);
  }, []);

  // ── Oferta ─────────────────────────────────────────────────────────────────

  const onTypeCustomDomain = useCallback((texto) => {
    const valor = (texto || '').trim().toLowerCase();
    setCustom({ input: valor, status: valor ? 'checking' : 'idle', message: null, selected: false, canUse: false });
    setActionFeedback(null);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!valor) return;

    debounceRef.current = setTimeout(async () => {
      ultimaBuscaRef.current = valor;
      try {
        const resposta = await domainsService.check(valor);
        // Resposta atrasada de um texto que ele já mudou: descartar em vez de piscar na tela.
        if (ultimaBuscaRef.current !== valor) return;
        const disponivel = Boolean(resposta?.available);
        const situacao = disponivel ? 'available'
          : resposta?.reason === 'taken' ? 'taken'
          : resposta?.reason === 'invalid_domain' ? 'invalid'
          : 'too_expensive';
        setCustom({
          // Adota o nome NORMALIZADO que o servidor devolveu ("sorrisoreal" → "sorrisoreal.com").
          // Sem isso, quem digita sem a extensão vê o preview errado e manda para a compra um
          // texto diferente do que foi checado.
          input: resposta?.domain || valor,
          status: situacao,
          message: disponivel ? null : (MOTIVO_LABEL[resposta?.reason] ?? null),
          selected: false,
          canUse: disponivel,
        });
      } catch (e) {
        if (ultimaBuscaRef.current !== valor) return;
        // 429 é o limite do fornecedor (1 consulta a cada ~10s na conta inteira), não uma falha:
        // dizer "tente de novo" faria o cliente insistir e tomar 429 outra vez.
        const bloqueado = e?.response?.status === 429;
        setCustom({
          input: valor,
          status: bloqueado ? 'throttled' : 'error',
          message: bloqueado
            ? 'Aguarde alguns segundos para conferir outro nome.'
            : 'Não conseguimos conferir agora. Tente de novo.',
          selected: false,
          canUse: false,
        });
      }
    }, DEBOUNCE_CHECAGEM_MS);
  }, []);

  const onSelectDomain = useCallback((domain) => {
    setSelectedDomain(domain);
    setActionFeedback(null);
  }, []);

  /**
   * Dispara a compra. `documento` só vai quando o cliente acabou de digitar o CPF no diálogo.
   * @returns {'ok'|'cpf'|'erro'} — 'cpf' = falta o dado, não é falha.
   */
  const comprar = useCallback(async (documento) => {
    if (!selectedDomain || !rootPageId) return 'erro';
    setBusy(true);
    setActionFeedback(null);
    try {
      const novo = await domainsService.purchase({
        domain: selectedDomain, rootPageId, ...(documento ? { document: documento } : {}),
      });
      setStatus(novo);
      setLidoEm(Date.now());
      setSelectedDomain(null);
      setCustom(CUSTOM_VAZIO);
      return 'ok';
    } catch (e) {
      const dados = e?.response?.data;
      if (/CPF/i.test(dados?.message || '')) return 'cpf';

      setActionFeedback({
        severity: 'error',
        message: ERRO_COMPRA_LABEL[dados?.error] ?? 'Não foi possível iniciar a compra. Tente de novo.',
        retryable: !dados?.error || dados.error === 'availability_unavailable',
      });
      // 409 significa que o servidor sabe de algo que a tela não mostrava (uma compra que já
      // existe). Reler é o que tira o cliente do beco: a compra aparece, com QR e saída.
      if (e?.response?.status === 409) await carregar(true);
      return 'erro';
    } finally {
      setBusy(false);
    }
  }, [selectedDomain, rootPageId, carregar]);

  const onStartPurchase = useCallback(async () => {
    if (await comprar(null) === 'cpf') setCpfDialog({ ...CPF_FECHADO, open: true });
  }, [comprar]);

  // ── CPF ────────────────────────────────────────────────────────────────────

  const onChangeCpf = useCallback((texto) => {
    setCpfDialog((d) => ({ ...d, value: cpfMask(texto || ''), error: null }));
  }, []);

  const onConfirmCpf = useCallback(async () => {
    const digitos = unmaskCpf(cpfDialog.value);
    if (!isValidCpf(digitos)) {
      setCpfDialog((d) => ({ ...d, error: 'Digite um CPF válido (11 dígitos).' }));
      return;
    }
    setCpfDialog((d) => ({ ...d, busy: true, error: null }));
    const resultado = await comprar(digitos);
    if (resultado === 'cpf') {
      setCpfDialog((d) => ({ ...d, busy: false, error: 'Não foi possível validar esse CPF. Confira os números.' }));
      return;
    }
    // Deu certo, ou falhou por outro motivo: nos dois casos o diálogo sai da frente. O aviso do
    // erro aparece no card, onde o cliente enxerga o contexto todo.
    setCpfDialog(CPF_FECHADO);
  }, [cpfDialog.value, comprar]);

  const onCloseCpf = useCallback(() => setCpfDialog(CPF_FECHADO), []);

  // ── Desistir ───────────────────────────────────────────────────────────────

  const onOpenCancelDialog = useCallback(() => setCancelDialog({ ...CANCEL_FECHADO, open: true }), []);
  const onCloseCancelDialog = useCallback(() => setCancelDialog(CANCEL_FECHADO), []);

  const onConfirmCancel = useCallback(async () => {
    const id = status?.openPurchase?.id;
    if (!id) return;
    setCancelDialog((d) => ({ ...d, busy: true, error: null }));
    setBusy(true);
    try {
      const novo = await domainsService.cancelPurchase(id);
      setStatus(novo);
      setLidoEm(Date.now());
      setCancelDialog(CANCEL_FECHADO);
      // A API não cancela o que já foi pago: devolve a compra como domínio em registro. Dizer
      // "cancelada" aqui seria mentir para quem acabou de pagar.
      const jaPago = (novo?.domains ?? []).some((d) => d.id === id);
      setActionFeedback(jaPago
        ? { severity: 'success', message: 'O pagamento já tinha sido confirmado, então a compra seguiu. O domínio é seu.', retryable: false }
        : { severity: 'info', message: 'Compra cancelada. Você pode escolher outro nome quando quiser.', retryable: false });
    } catch {
      setCancelDialog((d) => ({ ...d, busy: false, error: 'Não foi possível cancelar agora. Tente de novo.' }));
      await carregar(true);
    } finally {
      setBusy(false);
    }
  }, [status, carregar]);

  // ── Trocar a página da raiz (domínio já comprado) ──────────────────────────

  const onOpenRootPageDialog = useCallback((domainId) => {
    const alvo = (status?.domains || []).find((d) => d.id === domainId);
    setRootDialog({ ...RAIZ_FECHADO, open: true, domainId, selectedPageId: alvo?.rootPage?.id ?? null });
  }, [status]);

  const onCloseRootPageDialog = useCallback(() => setRootDialog(RAIZ_FECHADO), []);
  const onPickRootPage = useCallback((pageId) => setRootDialog((d) => ({ ...d, selectedPageId: pageId, error: null })), []);

  const onConfirmRootPage = useCallback(async () => {
    const { domainId, selectedPageId } = rootDialog;
    if (!domainId || !selectedPageId) return;
    setRootDialog((d) => ({ ...d, busy: true, error: null }));
    setBusy(true);
    try {
      setStatus(await domainsService.setRootPage(domainId, selectedPageId));
      setLidoEm(Date.now());
      setRootDialog(RAIZ_FECHADO);
      setActionFeedback({ severity: 'success', message: 'Pronto. O endereço principal foi atualizado.', retryable: false });
    } catch {
      setRootDialog((d) => ({ ...d, busy: false, error: 'Não foi possível trocar agora. Tente de novo.' }));
    } finally {
      setBusy(false);
    }
  }, [rootDialog]);

  const vm = montarVm({
    status, carregando, errorMessage, actionFeedback, busy, refreshing, dispensado, lidoEm,
    selectedDomain, rootPageId, custom, copiado, cpfDialog, cancelDialog, rootDialog,
  });

  const actions = {
    onSelectDomain,
    onTypeCustomDomain,
    onSelectRootPage: setRootPageId,
    onStartPurchase,
    onCopyPixCode: () => copiar(status?.openPurchase?.pix?.payload, 'pix'),
    onOpenCancelDialog,
    onCloseCancelDialog,
    onConfirmCancel,
    onChangeCpf,
    onConfirmCpf,
    onCloseCpf,
    onCopyUrl: (label) => copiar(label, label),
    onOpenRootPageDialog,
    onPickRootPage,
    onConfirmRootPage,
    onCloseRootPageDialog,
    onRefresh: () => carregar(false),
    onDismiss: () => setDispensado(true),
  };

  return { vm, actions };
}
